<script setup lang="ts">
import { VBtn, VCard, VCardActions, VCardText, VDialog, VSpacer, VThemeProvider } from 'vuetify/components'
import { computed, nextTick, onMounted, ref } from 'vue'
import type { Component, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { useEventListener } from '@vueuse/core'
const { t } = useI18n()
const vuetifyTheme = useTheme()

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: null,
  },
  titleComponent: {
    type: Object as PropType<Component>,
    required: false,
  },
  titleComponentProps: {
    type: Object,
    required: false,
  },
  content: {
    type: String,
    required: false,
    default: '',
  },
  contentComponent: {
    type: Object as PropType<Component>,
    required: false,
  },
  contentComponentProps: {
    type: Object,
    required: false,
  },
  confirmationKeyword: {
    type: String,
    required: false,
  },
  confirmationKeywordTextFieldProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  confirmationText: {
    type: String,
    required: false,
    default: null,
  },
  cancellationText: {
    type: String,
    required: false,
    default: null,
  },
  dialogProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  cardProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  cardTitleProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  cardTextProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  cardActionsProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  actionsContentComponent: {
    type: Object as PropType<Component>,
    required: false,
  },
  cancellationButtonProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  confirmationButtonProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  theme: {
    type: String,
    required: false,
    default: null,
  },
  resolve: {
    type: Function as PropType<(value: boolean) => void>,
    required: true,
  },
})

const isOpen = ref(true)
const textFieldInput = ref<HTMLInputElement | null>(null)
const textField = ref('')

function confirm() {
  props.resolve(true)
  isOpen.value = false
}

function cancel() {
  props.resolve(false)
  isOpen.value = false
}

onMounted(() => {
  textFieldInput.value?.focus()
})

useEventListener('keydown', (e) => {
  e.preventDefault() // make sure we don't enter something else
  if (e.key === 'Escape') {
    cancel()
  } else if (e.key === 'Enter' && !confirmationButtonDisabled.value) {
    confirm()
  }
})

const confirmationButtonDisabled = computed(() => {
  if (!props.confirmationKeyword) return false

  return props.confirmationKeyword !== textField.value
})

function resolveIfHidden(v: boolean) {
  if (!v) {
    nextTick(() => {
      props.resolve(false)
    })
  }
}
</script>

<template>
  <VThemeProvider :theme="theme ?? vuetifyTheme.name.value">
    <VDialog v-bind="dialogProps" v-model="isOpen" max-width="50vw" width="auto" @update:model-value="resolveIfHidden">
      <VCard v-bind="cardProps">
        <component :is="titleComponent" v-if="titleComponent" v-bind="titleComponentProps" />
        <VCardTitle v-else v-bind="cardTitleProps">
          {{ title ?? t('ConfirmDialogTitleDefault') }}
        </VCardTitle>
        <VCardText v-bind="cardTextProps">
          <component :is="contentComponent" v-if="contentComponent" v-bind="contentComponentProps" />
          <template v-else>
            <template v-if="content">
              {{ content }}
            </template>
            <VTextField
              v-if="confirmationKeyword"
              ref="textFieldInput"
              v-model="textField"
              v-bind="confirmationKeywordTextFieldProps"
              variant="underlined"
            />
          </template>
        </VCardText>
        <VCardActions v-bind="cardActionsProps">
          <component
            :is="actionsContentComponent"
            v-if="actionsContentComponent"
            :confirmation-button-disabled="confirmationButtonDisabled"
            :cancel="cancel"
            :confirm="confirm"
          />
          <template v-else>
            <VSpacer />
            <VBtn variant="flat" v-bind="cancellationButtonProps" @click="cancel">
              {{ cancellationText ?? t('ConfirmDialogButtonNo') }} (esc)
            </VBtn>
            <VBtn
              :disabled="confirmationButtonDisabled"
              variant="elevated"
              v-bind="confirmationButtonProps"
              @click="confirm"
            >
              {{ confirmationText ?? t('ConfirmDialogButtonYes') }} (enter)
            </VBtn>
          </template>
        </VCardActions>
      </VCard>
    </VDialog>
  </VThemeProvider>
</template>
